/* eslint-disable @typescript-eslint/no-explicit-any */
/* ────────────────────────────────────────────────────────────────────────
   SalaryCalculationGuide

   Fetches the authoritative salary calculation steps & formula from
   `api/salary/structure-guide` (a markdown document, same source the Salary
   Details screen uses) and renders it inside the structure-form summary rail
   so the HR user sees exactly how the salary will be calculated.

   Collapsed by default to keep the narrow summary panel tidy; fetched once and
   cached. Rendered with the shared ReactMarkdown + remark-gfm stack already in
   the project, with compact styles tuned for the ~340px rail.
   ──────────────────────────────────────────────────────────────────────── */
import React, { useState } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaChevronDown } from "react-icons/fa";
import apiService from "services/apiService";

// Compact markdown styling for the narrow summary rail (mirrors the Salary
// Details guide styling, scaled down). No `prose` plugin in this project, so
// spacing/borders are applied explicitly.
const guideMarkdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="text-[13px] font-bold text-slate-900 dark:text-white mt-3 mb-1 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-[12px] font-semibold text-slate-900 dark:text-white mt-3 mb-1 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-[12px] font-semibold text-slate-800 dark:text-slate-100 mt-2 mb-1">{children}</h3>
  ),
  p: ({ children }) => <p className="text-[11.5px] leading-relaxed text-slate-600 dark:text-slate-300 my-1">{children}</p>,
  ul: ({ children }) => (
    <ul className="list-disc pl-4 my-1 space-y-0.5 text-[11.5px] text-slate-600 dark:text-slate-300">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-4 my-1 space-y-0.5 text-[11.5px] text-slate-600 dark:text-slate-300">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900 dark:text-white">{children}</strong>,
  hr: () => <hr className="my-2 border-slate-200 dark:border-slate-700" />,
  code: ({ children }) => (
    <code className="px-1 py-0.5 rounded bg-slate-100 dark:bg-gray-700 font-mono text-[11px] text-slate-800 dark:text-slate-200">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="my-2 p-2 rounded-lg bg-slate-100 dark:bg-gray-700 overflow-x-auto font-mono text-[11px] text-slate-800 dark:text-slate-200 [&_code]:bg-transparent [&_code]:p-0">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full border-collapse text-[11px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50 dark:bg-gray-700/50">{children}</thead>,
  th: ({ children }) => (
    <th className="border border-slate-200 dark:border-slate-700 px-2 py-1 text-left font-semibold text-slate-700 dark:text-slate-200 align-top whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-slate-200 dark:border-slate-700 px-2 py-1 text-left align-top text-slate-600 dark:text-slate-300">
      {children}
    </td>
  ),
};

const SalaryCalculationGuide = () => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch the markdown guide once, on first expand (cached afterwards).
  const fetchGuide = () => {
    if (content || loading) return;
    setLoading(true);
    setError("");
    apiService
      .get("api/salary/structure-guide", {
        responseType: "text",
        headers: { Accept: "text/markdown" },
      })
      .then((response) => {
        setContent(typeof response.data === "string" ? response.data : String(response.data ?? ""));
      })
      .catch((err) => {
        console.error("Failed to fetch salary structure guide:", err);
        setError("Unable to load the calculation guide.");
      })
      .finally(() => setLoading(false));
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchGuide();
  };

  return (
    <div className="mt-3 rounded-[14px] border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <span className="text-sm font-bold text-slate-800 dark:text-white">Calculation Steps &amp; Formula</span>
        <FaChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 max-h-[360px] overflow-y-auto border-t border-slate-100 dark:border-gray-700 pt-2">
          {loading ? (
            <p className="text-[12px] text-slate-500 dark:text-slate-400 py-4 text-center">Loading calculation guide…</p>
          ) : error ? (
            <div className="py-4 text-center">
              <p className="text-[12px] text-red-500 mb-2">{error}</p>
              <button
                type="button"
                onClick={fetchGuide}
                className="text-[12px] font-medium text-blue-700 hover:underline"
              >
                Retry
              </button>
            </div>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={guideMarkdownComponents}>
              {content}
            </ReactMarkdown>
          )}
        </div>
      )}
    </div>
  );
};

export default SalaryCalculationGuide;
